import { useState, useEffect, useRef } from "react";
import { Routes, Route, useParams, Navigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import DashboardHome from "./DashboardHome";
// import Users from "./Users";
import Rooms from "../user-portal/pages/rooms/Rooms";
import AddNewRoom from "../user-portal/pages/rooms/AddNewRoom";
// import Storage from "./Storage";
import Recordings from "../user-portal/pages/recordings/Recordings";
import RecordingDetail from "../user-portal/pages/recordings/RecordingDetail";
import Profile from "../../components/auth-user/Profile";
import FolderList from "../user-portal/folders/FolderList";
import FileView from "../user-portal/folders/FileView";
import WidgetPanel from "./pages/elemets/widget/WidgetPanel";
import { NavLink } from "react-router-dom";
import ElementView from "./folders/ElementView";
import EditRoom from "./pages/rooms/EditRoom";
import EditScheduleMeeting from "./pages/rooms/EditScheduleMeeting";
import { ToastContainer } from "react-toastify";
import EditCtaTemplate from "./pages/elemets/widget/EditCtaTemplate";
import EditWebsite from "./pages/elemets/widget/EditWebsite";
import EditPrompter from "./pages/elemets/widget/EditPrompter";
import { useSiteContext } from "../../context/SiteContext";
import { useWhiteLabel } from "../../context/WhiteLabelContext";
import { LogOut, User, Settings } from "lucide-react";
import RLinkLogo from "../common/RLinkLogo";
// import AINotetaker from "./AINotetaker";
import WebinarScheduleForm from "./forms/WebinarScheduleForm";
// EditWebinarScheduleForm replaced — edit route now uses WebinarScheduleForm with roomId prop
import AINotetaker from "./AINotetaker";
import Sessions from "./pages/Sessions";
import CreatePolls from "./polls/CreatePolls";
import PollList from "./polls/PollList";
import EditPoll from "./polls/EditPoll";
import StartLanding from "./pages/StartLanding";
import MeetingSetup from "./pages/MeetingSetup";
import WhiteLabel from "./pages/WhiteLabel";
import LeadsTab from "./LeadsTab";
import FeedbackTab from "./FeedbackTab";
import ElementsTab from "./ElementsTab";
import NotetakerSettingsTab from "./NotetakerSettingsTab";
import Schedule from "./pages/Schedule";
import Account from "./pages/Account";
import Support from "./pages/Support";
import HelpSupport from "./pages/HelpSupport";
import BrandKitPage from "./pages/BrandKitPage";
import WebinarDashboard from "./pages/WebinarDashboard";
import VideoIntegrationSettings from "./pages/VideoIntegrationSettings";
import AffiliatePage from "./pages/affiliate/AffiliatePage";
import TeamUsers from "./pages/TeamUsers";
import EventLandingPages from "./pages/event-landing/EventLandingPages";
import BillingAndUsage from "./pages/BillingAndUsage";
import SignedContractsPage from "./pages/SignedContractsPage";
import MemberRoom from "./pages/member/MemberRoom";
import MemberProfile from "./pages/member/MemberProfile";
import MemberBilling from "./pages/member/MemberBilling";
import MemberSupport from "./pages/member/MemberSupport";
import MemberSupportTicket from "./pages/member/MemberSupportTicket";

// Wrapper so the edit route can pass roomId from URL params into the new wizard form
function EditWebinarWrapper() {
  const { roomId } = useParams();
  return <WebinarScheduleForm roomId={roomId} />;
}

const UserPortal = () => {
  const { user } = useSiteContext();
  const { branding, loading: whiteLabelLoading } = useWhiteLabel();
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= 768 : true
  );
  // const navigate = useNavigate();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Ensure sidebar is opened by default on desktop and when resizing to desktop
  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== "undefined" && window.innerWidth >= 768) {
        setSidebarOpen(true);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // const handleProfileClick = () => {
  //   navigate("/profile");
  // };

  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRefDesktop = useRef(null);
  const userMenuRefMobile = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      const clickedInsideDesktop =
        userMenuRefDesktop.current &&
        userMenuRefDesktop.current.contains(e.target);
      const clickedInsideMobile =
        userMenuRefMobile.current &&
        userMenuRefMobile.current.contains(e.target);
      if (!clickedInsideDesktop && !clickedInsideMobile) setShowUserMenu(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const headerLogout = () => {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("meetingRole");
      localStorage.removeItem("meetingId");
      localStorage.removeItem("meetingToken");
      localStorage.removeItem("meetingMicOn");
      localStorage.removeItem("meetingWebcamOn");
    } catch (_) {}
    window.location.href = "/login";
  };

  // Don't render until whitelabel loading is complete to prevent logo flash
  if (whiteLabelLoading) {
    return (
      <div className="flex min-h-screen w-full overflow-x-hidden portal-background" />
    );
  }

  const isTeamMember = user?.isTeamMember === true;

  return (
    <div className="flex min-h-screen w-full overflow-x-hidden portal-background">
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      {/* Mobile backdrop when sidebar open */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/40 md:hidden"
          onClick={toggleSidebar}
        />
      )}

      {/* Main Content */}
      <div
        className={`flex-1 flex flex-col transition-all duration-300 ml-0 ${
          sidebarOpen ? "md:ml-64" : "md:ml-16"
        }`}
      >
        <div className="w-full z-10">
          {/* Header (fixed, full-width, overlaps sidebar) */}
          <header className="fixed top-0 left-0 right-0 z-40 h-14 bg-[var(--bk-background,#001233)] border-b border-white/10">
            {/* Desktop layout */}
            <div className="hidden md:flex items-center justify-between h-full px-6">
              {/* Left: Logo */}
              <div className="flex items-center gap-3">
                <RLinkLogo size="md" companyLogo={branding.companyLogo} companyName={branding.companyName} />
              </div>
              {/* Right: User profile */}
              <div ref={userMenuRefDesktop} className="flex items-center gap-2">
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu((s) => !s)}
                    className="flex items-center gap-3 hover:bg-white/5 rounded-lg px-2 py-1.5 transition-colors"
                  >
                    {user?.logo ? (
                      <img
                        src={user.logo}
                        alt="Profile"
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--bk-primary,#6a1fbf)] to-[var(--bk-accent,#00c853)] flex items-center justify-center text-white font-semibold text-sm">
                        {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || "U"}
                      </div>
                    )}
                    <div className="text-sm text-left">
                      <p className="text-white font-medium leading-tight">{user?.name || "User"}</p>
                      <p className="text-gray-500 text-xs leading-tight">{user?.email}</p>
                    </div>
                  </button>
                  {showUserMenu && (
                    <div className="absolute right-0 top-full mt-2 w-48 rounded-lg shadow-xl py-1 z-[100] bg-[#0d1117] border border-white/10">
                      <NavLink
                        to="/dashboard/profile"
                        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <User className="w-4 h-4" />
                        Profile
                      </NavLink>
                      {user?.role !== 'member' && (
                        <NavLink
                          to="/dashboard/settings"
                          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <Settings className="w-4 h-4" />
                          Settings
                        </NavLink>
                      )}
                      <button
                        onClick={headerLogout}
                        className="w-full flex items-center gap-2 text-left px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* Mobile layout */}
            <div className="flex md:hidden items-center justify-between h-full px-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleSidebar}
                  className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <RLinkLogo size="sm" companyLogo={branding.companyLogo} companyName={branding.companyName} />
              </div>
              <div ref={userMenuRefMobile} className="relative">
                <button
                  onClick={() => setShowUserMenu((s) => !s)}
                  className="flex items-center gap-2 hover:bg-white/5 rounded-lg p-1.5 transition-colors"
                >
                  {user?.logo ? (
                    <img src={user.logo} alt="Profile" className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--bk-primary,#6a1fbf)] to-[var(--bk-accent,#00c853)] flex items-center justify-center text-white font-semibold text-sm">
                      {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || "U"}
                    </div>
                  )}
                </button>
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-44 rounded-lg shadow-xl py-1 z-50 bg-[#0d1117] border border-white/10">
                    <NavLink
                      to="/dashboard/profile"
                      className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <User className="w-4 h-4" />
                      Profile
                    </NavLink>
                    <button
                      onClick={headerLogout}
                      className="w-full flex items-center gap-2 text-left px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </header>

          {/* Main Content Area */}
          <main className="flex-1 overflow-y-auto overflow-x-hidden md:p-6 pt-14 md:pt-20 portal-background">
            <ToastContainer />
            <div className="w-full max-w-7xl mx-auto">
              <Routes>
                <Route path="/" element={<DashboardHome />} />
                <Route path="/start" element={<StartLanding />} />
                <Route path="/setup" element={<MeetingSetup />} />
                <Route path="/rooms" element={<Rooms />} />
                <Route path="/my-room" element={<MemberRoom />} />
                <Route path="/add-new-room" element={<AddNewRoom />} />
                <Route path="/edit-room/:roomId" element={<EditRoom />} />
                <Route
                  path="/edit-schedule-meeting/:roomId"
                  element={<EditScheduleMeeting />}
                />
                <Route
                  path="/edit-webinar/:roomId"
                  element={<EditWebinarWrapper />}
                />
                <Route path="/recordings" element={<Recordings />} />
                <Route path="/recordings/:recordingId" element={<RecordingDetail />} />

                <Route path="/profile" element={isTeamMember ? <MemberProfile /> : <Profile />} />

                <Route path="/folders" element={<FolderList />} />
                <Route
                  path="/fileview/:folderId/:category"
                  element={<FileView />}
                />

                <Route
                  path="/folders/element/:folderId"
                  element={<ElementView />}
                />

                <Route path="/elemets/:folderId" element={<WidgetPanel />} />

                <Route
                  path="/elemets/edit-cta/:folderId"
                  element={<EditCtaTemplate />}
                />

                <Route
                  path="/elements/edit-website/:folderId"
                  element={<EditWebsite />}
                />

                <Route
                  path="/elemets/edit-prompter/:folderId"
                  element={<EditPrompter />}
                />

                <Route path="/polls" element={<PollList />} />
                <Route path="/create-poll" element={<CreatePolls />} />

                <Route
                  path="/ai-notetaker/:sessionId"
                  element={<AINotetaker />}
                />
                <Route path="/sessions" element={<Sessions />} />
                <Route path="/schedule" element={<Schedule />} />
                <Route path="/account" element={<Account />} />
                <Route path="/help-support" element={<HelpSupport />} />
                <Route path="/support" element={isTeamMember ? <MemberSupport /> : <Support />} />
                <Route path="/support/ticket/:ticketId" element={isTeamMember ? <MemberSupportTicket /> : <Support />} />

                <Route path="/edit-poll/:pollId" element={<EditPoll />} />

                <Route
                  path="/webinar-detail"
                  element={<WebinarScheduleForm />}
                />
                <Route path="/integrations" element={<VideoIntegrationSettings />} />
                <Route path="/white-label" element={<WhiteLabel />} />
                <Route path="/leads" element={<LeadsTab />} />
                <Route path="/feedback" element={<FeedbackTab />} />
                <Route path="/elements" element={<ElementsTab />} />
                <Route path="/brand-kits" element={<BrandKitPage />} />
                <Route path="/webinars" element={<WebinarDashboard />} />
                <Route path="/signed-contracts" element={<SignedContractsPage />} />
                <Route path="/event-landing-pages" element={<EventLandingPages />} />
                <Route path="/ai-notetaker-settings" element={<NotetakerSettingsTab />} />
                <Route path="/billing" element={isTeamMember ? <MemberBilling /> : <BillingAndUsage />} />
                <Route path="/affiliate/*" element={<AffiliatePage />} />
                <Route
                  path="/team-users"
                  element={user?.isAdmin ? <TeamUsers /> : <Navigate to="/dashboard" replace />}
                />
                {/* <Route path="/settings" element={<Settings />} /> */}
                {/* <Route path="*" element={<Navigate to="/" replace />} /> */}
              </Routes>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default UserPortal;
